import { Component, Host, Prop, State, Watch, h } from "@stencil/core";

@Component({
    tag: "ux-iws-tab-tags",
    styleUrl: "tab-tags.scss",
    shadow: false,
})
export class TabTags {
    private scrollAnchorRef: HTMLDivElement;

    /** selector of the tab component that is linked to the tab tags */
    @Prop() tabsComponentSelector: string = "";
    /** the element taken as reference for the scrolling on tab click */
    @Prop() anchorReferenceElement: string = "";
    /** defines the max screen width for the component display and functionalities */
    @Prop() maxActivationWidth: number = 567;

    @State() tabButtons: NodeListOf<HTMLUxTabButtonElement>;
    /** reference to the screen width */
    @State() screenWidth: number;
    /** manages the activation of the normal tab header upon scrolling past component */
    @State() scrollAnchorRects: { top: number; bottom: number };

    private baseClass: string = "ux-iws-tab-tags";
    private tabComponent: Element;
    private searchBarComponent: HTMLUxIwsSearchBarElement;
    private anchorElementHeight: number;

    componentWillLoad() {
        const tabsPresent = this.setTabComponent();
        if (!tabsPresent) return;
        this.setSearchBarComponent();
        this.updateScreenWidth();
        window.addEventListener("resize", this.updateDimensions);
        this.contentObserver.observe(this.tabComponent, {
            childList: true,
            subtree: true,
            attributes: true,
        });
        window.addEventListener("scroll", this.updateAnchorRects, {
            passive: true,
        });
        window.addEventListener("click", this.handleTabActivation);
    }

    private handleTabActivation = (e: Event) => {
        const selectedTabId =
            (e.target as Element).getAttribute("data-ux-tab-target") ||
            (e.target as Element)
                .closest("a")
                ?.getAttribute("data-ux-tab-target");
        if (selectedTabId === "" || selectedTabId === undefined) return;
        e.preventDefault();
        const tabButtonsArray = Array.from(this.tabButtons);
        const button = tabButtonsArray.find(
            (tabButton) => tabButton.tabId === selectedTabId
        );
        if (this.screenWidth > this.maxActivationWidth) {
            button?.click();
        }
    };

    @Watch("screenWidth")
    activateComponentFunctionalities() {
        this.manageTabsScrollAttribute();
        this.updateAnchorRects();
        this.updateAnchorElementHeight();
    }

    @Watch("scrollAnchorRects")
    updateRegularTabNav() {
        if (!this.scrollAnchorRects) return;
        const container = document.querySelector(
            `${this.tabsComponentSelector}`
        ).parentElement;
        if (this.scrollAnchorRects.top <= 0) {
            container.classList.add("is-sticky");
        } else {
            container.classList.remove("is-sticky");
        }
    }

    private manageTabsScrollAttribute = () => {
        if (
            this.screenWidth <= this.maxActivationWidth &&
            !this.tabComponent.getAttribute("scroll-when-activated")
        ) {
            this.tabComponent.setAttribute(
                "scroll-when-activated",
                `.${this.baseClass}__anchor`
            );
            this.searchBarComponent.preventAutomaticScroll = true;
        } else if (
            this.screenWidth > this.maxActivationWidth &&
            this.tabComponent.getAttribute("scroll-when-activated")
        ) {
            this.tabComponent.removeAttribute("scroll-when-activated");
            this.searchBarComponent.preventAutomaticScroll = false;
        }
    };

    private updateDimensions = () => {
        this.updateScreenWidth();
        this.updateAnchorElementHeight();
    };

    private updateAnchorElementHeight = () => {
        const anchorReferenceComponent = document.querySelector(
            `${this.anchorReferenceElement}`
        );
        this.anchorElementHeight =
            anchorReferenceComponent.getBoundingClientRect().height;
    };

    private updateScreenWidth = () => {
        this.screenWidth = window.innerWidth;
    };

    private updateAnchorRects = () => {
        if (!this.scrollAnchorRef) return;
        this.scrollAnchorRects = this.scrollAnchorRef.getBoundingClientRect();
    };

    private setTabComponent = (): boolean => {
        this.tabComponent = document.querySelector(
            `${this.tabsComponentSelector}`
        );
        if (this.tabComponent === null) return false;
        else return true;
    };

    private setSearchBarComponent = () => {
        this.searchBarComponent = document
            .querySelector(`${this.tabsComponentSelector}`)
            ?.closest("ux-iws-search-bar");
    };

    private updateTabsTags = () => {
        this.tabButtons = this.tabComponent.querySelectorAll("ux-tab-button");
    };

    private contentObserver: MutationObserver = new MutationObserver(() =>
        this.updateTabsTags()
    );

    private renderTabTags = () => {
        if (this.tabButtons === undefined) return;
        const tabButtonsArray = Array.from(this.tabButtons);
        const tabTags = tabButtonsArray.map((tabButton) => {
            return (
                <div
                    class={{
                        [`${this.baseClass}__tag`]: true,
                        "is-active": tabButton.classList.contains("is-active"),
                    }}
                    data-ux-tab-target={tabButton.tabId}
                >
                    {tabButton.name}
                </div>
            );
        });

        return tabTags;
    };

    disconnectedCallback() {
        window.removeEventListener("resize", this.updateScreenWidth);
        window.removeEventListener("scroll", this.updateAnchorRects);
        this.contentObserver.disconnect();
    }

    render() {
        if (this.screenWidth > this.maxActivationWidth) {
            return (
                <Host
                    class={`${this.baseClass} ${this.baseClass}--hidden`}
                ></Host>
            );
        }

        return (
            <Host class={this.baseClass}>
                <div
                    class={`${this.baseClass}__anchor`}
                    style={{ bottom: `${this.anchorElementHeight - 10}px` }}
                    ref={(el) => (this.scrollAnchorRef = el)}
                ></div>
                {this.renderTabTags()}
            </Host>
        );
    }
}
